import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col, Button,
  Form,
  Offcanvas
} from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchBookings, selectBookingStatus,
  selectBookingError
} from "../../redux/bookingSlice";
import "bootstrap/dist/css/bootstrap.min.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTheme } from "@mui/material/styles";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import 'moment-timezone';
import jsPDF from 'jspdf';

// SVG Imports
import Calender from "../../assets/svgs/calender.svg";
import profilePlaceholder from "../../assets/svgs/user-placeholder.svg";
import idcard from "../../assets/svgs/id-icon.svg";
import sportsicon from "../../assets/sidepanel/sports.svg";
import priceicon from "../../assets/svgs/price-icon.svg";
import dateicon from "../../assets/svgs/date-icon.svg";
import timeicon from "../../assets/svgs/clock-min-icon.svg";
import facilityicon from "../../assets/svgs/flag-icon.svg";
import buttonimg from "../../assets/svgs/lines-icon.svg";
import gendericon from "../../assets/svgs/gender-icon.svg";
import smallcal from "../../assets/svgs/smallest-date-icon.svg";
import smallid from "../../assets/sidepanel/idcard.svg";
import facilityflag from "../../assets/sidepanel/flag.svg";
import pitch from "../../assets/sidepanel/pitch.svg";
import whatsapp from "../../assets/sidepanel/whatsapp.svg";
import players from "../../assets/sidepanel/players.svg";
import phoneicon from "../../assets/svgs/phone-icon.svg";
import emailicon from "../../assets/svgs/email.svg";
import CopyIcon from "../../assets/svgs/file.svg";

import "../bookings/bookings.css";
import { fetchAllFacilities } from "../../redux/facilitySlice";

// Helper functions for user type detection and data extraction
const isRandomUser = (booking) => {
  return booking.randomUser && !booking.bookedBy;
};

const getUserData = (booking) => {

  if (isRandomUser(booking)) {
    const userData = {
      name: booking.randomUser?.name || "N/A",
      phone: booking.randomUser?.phone || "N/A",
      email: booking.randomUser?.email || "N/A",
      notes: booking.randomUser?.notes || "N/A",
      profilePic: profilePlaceholder
    };
    return userData;
  } else {
    const userData = {
      name: booking.bookedBy?.firstName + " " + booking.bookedBy?.lastName || "N/A",
      gender: booking.bookedBy?.gender || "N/A",
      age: booking.bookedBy?.age || "N/A",
      email: booking.bookedBy?.email || "N/A",
      phone: booking.bookedBy?.phone || "N/A",
      profilePic: booking.bookedBy?.profilePic || profilePlaceholder
    };
    return userData;
  }
};

const getPitchName = (booking) => {
  if (isRandomUser(booking)) {
    const pitchName = booking.slot?.pitch?.name || "N/A";
    return pitchName;
  } else {
    const pitchName = booking.pitchDetail || "N/A";
    return pitchName;
  }
};

const getSportName = (booking) => {
  if (isRandomUser(booking)) {
    const sportName = booking.slot?.sport?.name || "N/A";
    return sportName;
  } else {
    const sportName = booking.sportName || "N/A";
    return sportName;
  }
};

const getFacilityName = (booking, facilities) => {
  if (isRandomUser(booking)) {
    const facilityName = booking.slot?.facility?.name || "N/A";
    return facilityName;
  } else {
    const getFacilityNameById = (targetId) => {
      if (!facilities || !Array.isArray(facilities)) return 'N/A';
      const match = facilities.find(facility => facility._id === targetId);
      return match ? match.name : 'N/A';
    };
    const facilityName = getFacilityNameById(booking.facilityId) || "N/A";
    return facilityName;
  }
};

// Helper: determine if booking is current or past
const isBookingPast = (booking) => {
  if (!booking) return false;

  try {
    // Get the booking date and end time
    const bookingDate = booking.selectedDate || booking.date;
    const endTime = booking.endTime || booking.timeSlot?.to;

    if (!bookingDate || !endTime) return false;

    // Create a moment object for the booking end time
    let bookingDateTime;

    // If endTime is already a time string (e.g., "09:00 PM"), combine with date
    if (typeof endTime === 'string' && endTime.match(/^\d{1,2}:\d{2}\s?(AM|PM)?$/i)) {
      const dateStr = moment(bookingDate).format('YYYY-MM-DD');
      bookingDateTime = moment(`${dateStr} ${endTime}`, 'YYYY-MM-DD hh:mm A');
    } else {
      // If endTime is already a datetime string
      bookingDateTime = moment(endTime);
    }

    // Check if the booking end time has passed
    return bookingDateTime.isBefore(moment());
  } catch (error) {
    console.error('Error determining booking time:', error);
    return false;
  }
};


// BookingCard Component
const BookingCard = ({ booking, isLoading, onViewDetails }) => {
  const [imgError, setImgError] = useState(false);
  const { facilities } = useSelector((state) => state.facilities);

  // ---- Skeleton / missing-data guard --------------------------------
  // Render this before computing anything from `booking`. isLoading
  // normally implies booking is undefined, but we also guard against
  // booking being missing for any other reason (bad data, race, etc.)
  if (isLoading || !booking) {
    return (
      <div className="booking-card-modern">
        <div className="booking-card-header">
          <div className="booking-user">
            <Skeleton circle width={44} height={44} />
            <div className="booking-user-info">
              <Skeleton width={110} height={16} />
              <Skeleton width={65} height={11} className="mt-1" />
            </div>
          </div>
        </div>

        <div className="booking-id-section">
          <div className="booking-id-label">Booking ID</div>
          <Skeleton width={100} height={18} />
        </div>

        <div className="booking-main-info">
          <div className="booking-info-item">
            <div className="booking-info-content">
              <span className="booking-info-title">Sport</span>
              <Skeleton width={75} height={15} />
            </div>
          </div>
          <div className="booking-info-item">
            <div className="booking-info-content">
              <span className="booking-info-title">Price</span>
              <Skeleton width={75} height={15} />
            </div>
          </div>
          <div className="booking-info-item">
            <div className="booking-info-content">
              <span className="booking-info-title">Facility</span>
              <Skeleton width={85} height={15} />
            </div>
          </div>
        </div>

        <div className="booking-schedule">
          <div className="schedule-item">
            <div>
              <span className="booking-card-schedule-label">Date</span>
              <Skeleton width={70} height={15} />
            </div>
          </div>
          <div className="schedule-divider" />
          <div className="schedule-item">
            <div>
              <span className="booking-card-schedule-label">Time</span>
              <Skeleton width={110} height={15} />
            </div>
          </div>
        </div>

        <div className="booking-card-footer">
          <Skeleton width="100%" height={40} />
        </div>
      </div>
    );
  }

  // ---- Everything below only runs once `booking` is guaranteed ------
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return moment(dateString).format("DD-MMM");
    } catch (error) {
      return dateString;
    }
  };

  function formatTimeLocal(dateString) {
    if (!dateString) return "N/A";

    const simpleTimeRegex = /^[0-9]{1,2}:[0-9]{2}(\s?[APMapm]{2})?$/;
    if (simpleTimeRegex.test(dateString.trim())) {
      return dateString;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }

    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHour = hours % 12 || 12;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

    return `${formattedHour}:${formattedMinutes} ${ampm}`;
  }

  const formatTimeRange = (start, end) => {
    if (!start || !end) return "N/A";
    return `${formatTimeLocal(start)} - ${formatTimeLocal(end)}`;
  };

  const userData = getUserData(booking);
  const randomUser = isRandomUser(booking);

  const bookingType = randomUser
    ? "Reserved"
    : booking.isMultipleCombine
      ? "Multi-Slot"
      : "Single-Slot";

  const cancellationLabel =
    booking.cancellationType === "user"
      ? "User Cancelled"
      : booking.cancellationType === "facility"
        ? "Facility Cancelled"
        : "Cancelled";

  return (
    <div className="booking-card-modern">

      {/* Header */}
      <div className="booking-card-header">

        <div className="booking-user">

          <img
            src={!imgError ? userData.profilePic : profilePlaceholder}
            alt="Profile"
            className="booking-profile-img"
            onError={() => setImgError(true)}
          />

          <div className="booking-user-info">
            <h5 className="booking-user-name">
              {userData.name || "Unknown User"}
            </h5>
            <span className="booking-user-label">Booked by</span>
          </div>
        </div>

        <div className="booking-status-group">

          <span
            className={`booking-type-badge ${randomUser
              ? "reserved"
              : booking.isMultipleCombine
                ? "multi"
                : "single"
              }`}
          >
            <span className="badge-dot" />
            {bookingType}
          </span>

          {booking.isCancelled && (
            <span className="booking-cancelled-badge">
              {cancellationLabel}
            </span>
          )}

        </div>

      </div>

      {/* Booking ID */}
      <div className="booking-id-section">
        <div className="booking-id-inner-section">
          <div className="booking-id-label">Booking ID</div>
          <div className="booking-id-value">
            <span>#{booking.bookingId || "N/A"}</span>
          </div>
        </div>
      </div>

      {/* Main information */}
      <div className="booking-main-info">

        {/* Sport */}
        <div className="booking-info-item">
          <div className="booking-info-icon">
            <img src={sportsicon} alt="" />
          </div>
          <div className="booking-info-content">
            <span className="booking-info-title">Sport</span>
            <span className="booking-info-value">
              {getSportName(booking) || "N/A"}
            </span>
          </div>
        </div>

        {/* Price */}
        <div className="booking-info-item">
          <div className="booking-info-icon">
            <img src={priceicon} alt="" />
          </div>
          <div className="booking-info-content">
            <span className="booking-info-title">Price</span>
            <span className="booking-info-value price">
              {`${booking.price || 0} PKR`}
            </span>
          </div>
        </div>

        {/* Date */}
        <div className="booking-info-item">
          <div className="booking-info-icon">
            <img src={dateicon} alt="" />
          </div>

          <div className="booking-info-content">
            <span className="booking-info-title">Date</span>
            <span className="booking-info-value">
              {formatDate(booking.selectedDate)}
            </span>
          </div>
        </div>

      </div>

      {/* Schedule */}
      <div className="booking-card-booking-schedule">

        <div className="booking-card-schedule-item">
          <img src={facilityicon} alt="" />
          <div>
            <span
              className="booking-card-schedule-label"
            >
              Facility
            </span>
            <span
              className="booking-card-schedule-value"
              title={getFacilityName(booking, facilities)}
            >
              {getFacilityName(booking, facilities) || "N/A"}
            </span>
          </div>
        </div>

        <div className="booking-card-schedule-divider" />

        <div className="booking-card-schedule-item">
          <img src={timeicon} alt="" />
          <div>
            <span className="booking-card-schedule-label">Time</span>
            <span className="booking-card-schedule-value">
              {formatTimeRange(booking.startTime, booking.endTime)}
            </span>
          </div>
        </div>

      </div>

      {/* Action */}
      <div className="booking-card-footer">
        <Button
          variant="primary"
          className="booking-details-btn"
          onClick={() => onViewDetails(booking)}
        >
          View Booking Details
          <span className="booking-arrow">→</span>
        </Button>
      </div>

    </div>
  );
};


// Add BookingDetailsSkeleton component at the top with other components
const BookingDetailsSkeleton = () => (
  <div className="booking-details">
    {/* Booking ID Header */}
    <div className="canvas-title d-flex align-items-center">
      <Skeleton width={120} height={24} style={{ marginRight: '8px' }} />
      <Skeleton width={80} height={24} />
    </div>

    {/* Booked By Section */}
    <h4 className="side-panel-titles">
      <Skeleton width={100} height={24} />
    </h4>
    <div className="booked-by">
      <div className="wrap-name">
        <Skeleton circle width={40} height={40} />
        <Skeleton width={120} height={24} style={{ margin: '8px' }} />
      </div>

      <div className="other-info m-3">
        <Row>
          <Col lg={2} className="d-flex justify-content-center m-3">
            <div className="personal-data-wrap">
              <Skeleton width={24} height={24} style={{ marginBottom: '8px' }} />
              <Skeleton width={60} height={16} style={{ marginBottom: '4px' }} />
              <Skeleton width={40} height={16} />
            </div>
          </Col>
          <Col lg={6} className="d-flex justify-content-center m-3">
            <div className="personal-data-wrap">
              <Skeleton width={24} height={24} style={{ marginBottom: '8px' }} />
              <Skeleton width={60} height={16} style={{ marginBottom: '4px' }} />
              <Skeleton width={40} height={16} />
            </div>
          </Col>
        </Row>
      </div>
    </div>

    {/* Booking Information Section */}
    <h4 className="side-panel-titles">
      <Skeleton width={160} height={24} />
    </h4>
    <div className="booking-information">
      <Row>
        {[...Array(4)].map((_, rowIndex) => (
          <React.Fragment key={rowIndex}>
            <Col lg={6}>
              <div className="info-item">
                <Skeleton width={24} height={24} style={{ marginRight: '8px' }} />
                <Skeleton width={100} height={16} />
              </div>
              <Skeleton width={80} height={16} style={{ marginTop: '4px' }} />
            </Col>
            <Col lg={6}>
              <div className="info-item">
                <Skeleton width={24} height={24} style={{ marginRight: '8px' }} />
                <Skeleton width={100} height={16} />
              </div>
              <Skeleton width={80} height={16} style={{ marginTop: '4px' }} />
            </Col>
            {rowIndex < 3 && <hr />}
          </React.Fragment>
        ))}
      </Row>
    </div>

    {/* Schedule Section */}
    <Row>
      <h4 className="side-panel-titles">
        <Skeleton width={100} height={24} />
      </h4>
      <Col lg={10}>
        <Skeleton width={200} height={32} style={{ marginBottom: '16px' }} />
        <div className="schedule-wrap m-4">
          <Skeleton width={24} height={24} style={{ marginBottom: '8px' }} />
          <Skeleton width={160} height={16} style={{ marginBottom: '16px' }} />
          <Row>
            <Col lg={6}>
              <div className="id-wrap">
                <Skeleton width={24} height={24} style={{ marginBottom: '8px' }} />
                <Skeleton width={80} height={16} style={{ marginBottom: '4px' }} />
              </div>
              <Skeleton width={100} height={16} />
            </Col>
            <Col lg={6}>
              <div className="id-wrap">
                <Skeleton width={24} height={24} style={{ marginBottom: '8px' }} />
                <Skeleton width={80} height={16} style={{ marginBottom: '4px' }} />
              </div>
              <Skeleton width={100} height={16} />
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
  </div>
);

const MyBookings = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const bookings = useSelector((state) => state.bookings?.bookings || []);
  const status = useSelector(selectBookingStatus);
  const error = useSelector(selectBookingError);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const cardsPerPage = 3;
  const [currentPage, setCurrentPage] = useState(1);
  const bookingsPerPage = 6; // Show 6 bookings (2 rows of 3)
  const navigate = useNavigate();

  // Add filter states
  const [searchId, setSearchId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedSport, setSelectedSport] = useState('');
  const [selectedFacility, setSelectedFacility] = useState('');
  const [isFiltering, setIsFiltering] = useState(false);

  // Add booking type tab state
  const [activeTab, setActiveTab] = useState('current'); // 'current', 'past', or 'cancelled'

  const { facilities } = useSelector((state) => state.facilities);

  // Get unique sports from bookings
  const uniqueSports = [...new Set(bookings.map(booking => booking.sportName))].filter(Boolean);

  // Get unique facilities
  const uniqueFacilities = facilities || [];

  // Get unique months from bookings
  const uniqueMonths = [...new Set(bookings.map(booking => {
    if (!booking.selectedDate) return null;
    const date = new Date(booking.selectedDate);
    return `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
  }))].filter(Boolean);

  // Filter bookings based on all criteria
  const filteredBookings = bookings.filter(booking => {
    // Filter by booking type (current/past/cancelled)
    const isPast = isBookingPast(booking);
    const isCancelled = booking.isCancelled === true;

    if (activeTab === 'current' && (isPast || isCancelled)) {
      return false;
    }
    if (activeTab === 'past' && (!isPast || isCancelled)) {
      return false;
    }
    if (activeTab === 'cancelled' && !isCancelled) {
      return false;
    }

    // Filter by booking ID - convert both to strings for comparison
    if (searchId && !String(booking.bookingId).toLowerCase().includes(searchId.toLowerCase())) {
      return false;
    }

    // Filter by month
    if (selectedMonth) {
      const bookingDate = new Date(booking.selectedDate);
      const bookingMonthYear = `${bookingDate.toLocaleString('default', { month: 'long' })} ${bookingDate.getFullYear()}`;
      if (bookingMonthYear !== selectedMonth) {
        return false;
      }
    }

    // Filter by sport
    if (selectedSport && booking.sportName !== selectedSport) {
      return false;
    }

    // Filter by facility
    if (selectedFacility && booking.facilityId !== selectedFacility) {
      return false;
    }

    return true;
  });

  const notify = (message, type = 'info') => {
    // Remove all existing toasts
    toast.dismiss();

    // Wait a tiny bit before showing new toast
    setTimeout(() => {
      const toastId = type === 'success' ?
        toast.success(message, {
          toastId: 'success-toast',
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          closeButton: false
        }) :
        toast.error(message, {
          toastId: 'error-toast',
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
          closeButton: false
        });
    }, 100);
  };

  // Helper function to generate Google Maps link from facility location
  const generateGoogleMapsLink = (booking) => {
    try {
      let lat, lng;

      if (isRandomUser(booking)) {
        // For random users, get location from slot.facility.location
        lat = booking.slot?.facility?.location?.lat;
        lng = booking.slot?.facility?.location?.lng;
      } else {
        // For app users, find facility by ID and get location
        const facility = facilities?.find(f => f._id === booking.facilityId);
        lat = facility?.location?.lat;
        lng = facility?.location?.lng;
      }

      if (lat && lng) {
        // Generate Google Maps link using coordinates
        return `https://maps.google.com/maps?q=${lat},${lng}`;
      }

      return null;
    } catch (error) {
      console.error("Error generating Google Maps link:", error);
      return null;
    }
  };

  const copyBookingToClipboard = async () => {

    try {
      // Generate Google Maps link
      const googleMapsLink = generateGoogleMapsLink(selectedBooking);

      // Format the booking information as requested
      const bookingText = `
      Booking ID: ${selectedBooking.bookingId}
      Date: ${formatDetailDate(selectedBooking.selectedDate)}
      Time: ${formatTime(selectedBooking.startTime)} - ${formatTime(selectedBooking.endTime)}
      Facility: ${getFacilityName(selectedBooking, facilities) || "N/A"}
      Sports: ${getSportName(selectedBooking)}
      Pitch: ${getPitchName(selectedBooking)}
      Price: ${selectedBooking.price} 'PKR'
      ${googleMapsLink ? `

${googleMapsLink}` : ''}
      `;

      // Use the modern clipboard API
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(bookingText);
        notify("Booking information copied to clipboard!", "success");
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement("textarea");
        textArea.value = bookingText;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          document.execCommand('copy');
          notify("Slot information copied to clipboard!", "success");
        } catch (err) {
          notify("Failed to copy to clipboard", "error");
        }

        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      notify("Failed to copy to clipboard", "error");
    }
  };

  // Helper function to sanitize text for PDF (removes problematic characters)
  const sanitizeTextForPDF = (text) => {
    if (!text || text === 'N/A') return 'N/A';
    try {
      // First check if it's an array and join it
      if (Array.isArray(text)) {
        text = text.join('');
      }

      // Convert to string and ensure it's a proper string
      let sanitized = String(text);

      // Remove null bytes and other control characters that cause issues
      sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');

      // Remove emojis and other non-ASCII symbols that jsPDF can't render properly
      // This regex removes emojis and other high unicode characters (above U+024F)
      sanitized = sanitized.replace(/[\u{1F300}-\u{1F9FF}]/gu, ''); // Emoji range
      sanitized = sanitized.replace(/[\u{1FA00}-\u{1FAFF}]/gu, ''); // Extended emoji range
      sanitized = sanitized.replace(/[\u{2600}-\u{26FF}]/gu, ''); // Miscellaneous symbols
      sanitized = sanitized.replace(/[\u{2700}-\u{27BF}]/gu, ''); // Dingbats

      // Replace problematic unicode characters that jsPDF can't handle well
      sanitized = sanitized.replace(/[\u200B-\u200D\uFEFF]/g, ''); // Zero-width characters

      // Normalize unicode characters (NFD to NFC) to fix encoding issues
      if (typeof sanitized.normalize === 'function') {
        sanitized = sanitized.normalize('NFC');
      }

      // Remove any remaining problematic characters that might cause spacing issues
      sanitized = sanitized.replace(/[\u00AD\u2000-\u200A\u2028\u2029]/g, ' '); // Various spaces and breaks

      // Replace multiple spaces with single space
      sanitized = sanitized.replace(/\s+/g, ' ');

      // Trim and return
      sanitized = sanitized.trim();
      return sanitized || 'N/A';
    } catch (error) {
      console.error('Error sanitizing text for PDF:', error);
      return 'N/A';
    }
  };

  // Helper function to get facility location
  const getFacilityLocation = (booking, facilities) => {
    try {
      if (isRandomUser(booking)) {
        // For random users, get location from slot.facility.location
        const location = booking.slot?.facility?.location;
        if (location) {
          // If there's an address field, use it; otherwise format coordinates
          if (location.address) {
            return location.address;
          } else if (location.lat && location.lng) {
            return `${location.lat}, ${location.lng}`;
          }
        }
        return 'N/A';
      } else {
        // For app users, find facility by ID and get location
        const facility = facilities?.find(f => f._id === booking.facilityId);
        if (facility?.location) {
          // If there's an address field, use it; otherwise format coordinates
          if (facility.location.address) {
            return facility.location.address;
          } else if (facility.location.lat && facility.location.lng) {
            return `${facility.location.lat}, ${facility.location.lng}`;
          }
        }
        return 'N/A';
      }
    } catch (error) {
      console.error('Error getting facility location:', error);
      return 'N/A';
    }
  };

  // Helper function to get facility object
  const getFacilityObject = (booking, facilities) => {
    try {
      return facilities?.find(f => f._id === booking.facilityId) || null;
    } catch (error) {
      console.error('Error getting facility object:', error);
      return null;
    }
  };

  // Export booking to PDF as professional invoice/receipt
  const exportBookingToPDF = (booking) => {
    try {
      if (!booking) {
        notify("No booking selected for export", "error");
        return;
      }

      // Get facility name early for header
      let facilityNameForHeader = getFacilityName(booking, facilities) || 'N/A';
      if (Array.isArray(facilityNameForHeader)) {
        facilityNameForHeader = facilityNameForHeader.join('');
      }
      facilityNameForHeader = String(facilityNameForHeader);
      let sanitizedFacilityNameForHeader = sanitizeTextForPDF(facilityNameForHeader);
      sanitizedFacilityNameForHeader = sanitizedFacilityNameForHeader
        .replace(/[\u200B-\u200D\uFEFF\u00AD]/g, '')
        .replace(/[\u2000-\u200A\u2028\u2029]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      // A5 size: 148mm x 210mm (portrait)
      const doc = new jsPDF('p', 'mm', 'a5');
      const pageWidth = 148;
      const pageHeight = 210;
      let yPosition = 15;

      // Colors
      const primaryColor = [56, 176, 0]; // #38b000
      const darkColor = [33, 37, 41]; // #212529
      const lightGray = [245, 245, 245];
      const borderGray = [200, 200, 200];

      // Header Section with colored background (reduced height)
      const headerHeight = 25;
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, headerHeight, 'F');

      // Facility Name (replacing GoPlay) - uppercase, smaller size
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      const facilityNameUpper = (sanitizedFacilityNameForHeader || 'FACILITY').toUpperCase();
      doc.text(facilityNameUpper, pageWidth / 2, 12, { align: 'center' });

      // Invoice/Receipt Label - uppercase, smaller size
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('BOOKING CONFIRMATION', pageWidth / 2, 18, { align: 'center' });

      yPosition = headerHeight + 8;

      // Invoice Number and Date Section - titles bold, values normal
      doc.setTextColor(...darkColor);
      doc.setFontSize(10);

      // ✅ CHANGED: Use today's date instead of booking date
      const todayDate = formatDetailDate(new Date().toISOString().split('T')[0]);

      // Booking ID - title bold, value normal
      doc.setFont('helvetica', 'bold');
      doc.text('Booking ID #: ', 10, yPosition);
      const bookingIdX = 10 + doc.getTextWidth('Booking ID #: ');
      doc.setFont('helvetica', 'normal');
      doc.text(`${booking.bookingId || 'N/A'}`, bookingIdX, yPosition);

      // ✅ CHANGED: Date shows today's date
      const dateLabel = 'Invoice Date: ';
      doc.setFont('helvetica', 'bold');
      const dateValueWidth = doc.getTextWidth(todayDate);
      const dateLabelWidth = doc.getTextWidth(dateLabel);
      const dateX = pageWidth - 10 - dateValueWidth - dateLabelWidth;
      doc.text(dateLabel, dateX, yPosition);
      doc.setFont('helvetica', 'normal');
      doc.text(todayDate, dateX + dateLabelWidth, yPosition);

      yPosition += 4;

      // Line separator
      doc.setDrawColor(...borderGray);
      doc.line(10, yPosition, pageWidth - 10, yPosition);
      yPosition += 10;

      // Side-by-side layout: Customer Details (left) and Booking Details (right aligned to page end)
      const leftX = 10;
      const rightColumnX = pageWidth / 2 + 15;
      const rightColumnWidth = pageWidth - rightColumnX - 10;
      const startY = yPosition;

      // Customer Details Section (Left Side)
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('CUSTOMER DETAILS', leftX, startY);
      let customerY = startY + 7;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const userData = getUserData(booking);

      doc.text(`Name: ${sanitizeTextForPDF(userData.name)}`, leftX, customerY);
      customerY += 5;

      if (!isRandomUser(booking)) {
        if (userData.gender && userData.gender !== 'N/A') {
          doc.text(`Gender: ${sanitizeTextForPDF(userData.gender)}`, leftX, customerY);
          customerY += 5;
        }
        if (userData.age && userData.age !== 'N/A') {
          doc.text(`Age: ${sanitizeTextForPDF(userData.age)}`, leftX, customerY);
          customerY += 5;
        }
        if (userData.email && userData.email !== 'N/A') {
          doc.text(`Email: ${sanitizeTextForPDF(userData.email)}`, leftX, customerY);
          customerY += 5;
        }
      }

      const phoneNumber = userData.phone && userData.phone !== 'N/A' ? userData.phone : (isRandomUser(booking) ? (booking.randomUser?.phone || 'N/A') : 'N/A');
      if (phoneNumber) {
        doc.text(`Phone: ${sanitizeTextForPDF(phoneNumber)}`, leftX, customerY);
        customerY += 5;
      }

      const email = userData.email && userData.email !== 'N/A' ? userData.email : (isRandomUser(booking) ? (booking.randomUser?.email || 'N/A') : 'N/A');
      if (email) {
        doc.text(`Email: ${sanitizeTextForPDF(email)}`, leftX, customerY);
        customerY += 5;
      }

      let bookingY = startY;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('BOOKING DETAILS', rightColumnX, bookingY, { align: 'left' });
      bookingY += 7;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');

      // ✅ NEW: Show booking date in booking details
      const bookingDateFormatted = formatDetailDate(booking.selectedDate);
      doc.text(`Booking Date: ${sanitizeTextForPDF(bookingDateFormatted)}`, rightColumnX, bookingY, { align: 'left', maxWidth: rightColumnWidth });
      bookingY += 5;

      const bookingTime = booking.startTime && booking.endTime
        ? `${formatTime(booking.startTime)} - ${formatTime(booking.endTime)}`
        : 'N/A';

      doc.text(`Time: ${sanitizeTextForPDF(bookingTime)}`, rightColumnX, bookingY, { align: 'left', maxWidth: rightColumnWidth });
      bookingY += 5;
      doc.text(`Sport: ${sanitizeTextForPDF(getSportName(booking))}`, rightColumnX, bookingY, { align: 'left', maxWidth: rightColumnWidth });
      bookingY += 5;

      let facilityName = getFacilityName(booking, facilities) || 'N/A';
      if (Array.isArray(facilityName)) {
        facilityName = facilityName.join('');
      }
      facilityName = String(facilityName);
      let sanitizedFacility = sanitizeTextForPDF(facilityName);
      sanitizedFacility = sanitizedFacility
        .replace(/[\u200B-\u200D\uFEFF\u00AD]/g, '')
        .replace(/[\u2000-\u200A\u2028\u2029]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      const facilityText = `Facility: ${sanitizedFacility}`;
      doc.text(facilityText, rightColumnX, bookingY, { align: 'left', maxWidth: rightColumnWidth });
      bookingY += 5;

      doc.text(`Pitch/Court: ${sanitizeTextForPDF(getPitchName(booking))}`, rightColumnX, bookingY, { align: 'left', maxWidth: rightColumnWidth });
      bookingY += 5;

      yPosition = Math.max(customerY, bookingY) + 8;

      // Slot Details Section Heading
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkColor);
      doc.text('SLOT DETAILS', leftX, yPosition);
      yPosition += 7;

      const timeSlots = getBookingTimeSlots(booking);

      if (timeSlots.length > 0) {
        const useTwoColumns = timeSlots.length > 5;
        const slotStartY = yPosition;

        if (useTwoColumns) {
          // Two-column layout for more than 5 slots
          const columnWidth = (pageWidth - 30) / 2; // Two columns with spacing
          const leftColumnX = 10;
          const rightColumnX = pageWidth / 2 + 5;
          const columnHeaderHeight = 8;
          const rowHeight = 6;

          // Calculate how many slots per column
          const slotsPerColumn = Math.ceil(timeSlots.length / 2);
          const leftColumnSlots = timeSlots.slice(0, slotsPerColumn);
          const rightColumnSlots = timeSlots.slice(slotsPerColumn);

          // Left Column Header
          doc.setFillColor(...lightGray);
          doc.rect(leftColumnX, slotStartY - 5, columnWidth, columnHeaderHeight, 'F');
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...darkColor);
          doc.text('Time Slot', leftColumnX + 2, slotStartY);
          doc.text('Price', leftColumnX + columnWidth - 2, slotStartY, { align: 'right' });

          // Right Column Header
          doc.setFillColor(...lightGray);
          doc.rect(rightColumnX, slotStartY - 5, columnWidth, columnHeaderHeight, 'F');
          doc.text('Time Slot', rightColumnX + 2, slotStartY);
          doc.text('Price', rightColumnX + columnWidth - 2, slotStartY, { align: 'right' });

          let currentY = slotStartY + columnHeaderHeight;
          doc.setFont('helvetica', 'normal');

          // Render slots in both columns
          const maxRows = Math.max(leftColumnSlots.length, rightColumnSlots.length);

          for (let i = 0; i < maxRows; i++) {
            // Alternate row background
            if (i % 2 === 0) {
              doc.setFillColor(250, 250, 250);
              doc.rect(leftColumnX, currentY - 4, columnWidth, rowHeight, 'F');
              doc.rect(rightColumnX, currentY - 4, columnWidth, rowHeight, 'F');
            }

            // Left column slot
            if (i < leftColumnSlots.length) {
              const slot = leftColumnSlots[i];
              const slotTime = `${formatTime(slot.from)} - ${formatTime(slot.to)}`;
              const slotPrice = `${slot.price || booking.price || 0} PKR`;
              doc.setTextColor(...darkColor);
              doc.text(sanitizeTextForPDF(slotTime), leftColumnX + 2, currentY);
              doc.text(sanitizeTextForPDF(slotPrice), leftColumnX + columnWidth - 2, currentY, { align: 'right' });
            }

            // Right column slot
            if (i < rightColumnSlots.length) {
              const slot = rightColumnSlots[i];
              const slotTime = `${formatTime(slot.from)} - ${formatTime(slot.to)}`;
              const slotPrice = `${slot.price || booking.price || 0} PKR`;
              doc.setTextColor(...darkColor);
              doc.text(sanitizeTextForPDF(slotTime), rightColumnX + 2, currentY);
              doc.text(sanitizeTextForPDF(slotPrice), rightColumnX + columnWidth - 2, currentY, { align: 'right' });
            }

            currentY += rowHeight;
          }

          yPosition = currentY + 3;
        } else {
          // Single column layout for 5 or fewer slots
          doc.setFillColor(...lightGray);
          doc.rect(10, yPosition - 5, pageWidth - 20, 8, 'F');

          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...darkColor);
          doc.text('Time Slot', 12, yPosition);
          doc.text('Price', pageWidth - 12, yPosition, { align: 'right' });
          yPosition += 8;

          doc.setFont('helvetica', 'normal');
          timeSlots.forEach((slot, index) => {
            const slotTime = `${formatTime(slot.from)} - ${formatTime(slot.to)}`;
            const slotPrice = `${slot.price || booking.price || 0} PKR`;

            if (index % 2 === 0) {
              doc.setFillColor(250, 250, 250);
              doc.rect(10, yPosition - 4, pageWidth - 20, 6, 'F');
            }

            doc.setTextColor(...darkColor);
            doc.text(sanitizeTextForPDF(slotTime), 12, yPosition);
            doc.text(sanitizeTextForPDF(slotPrice), pageWidth - 12, yPosition, { align: 'right' });
            yPosition += 6;
          });

          yPosition += 3;
        }
      }

      doc.setDrawColor(...borderGray);
      doc.line(10, yPosition, pageWidth - 10, yPosition);
      yPosition += 8;

      let totalPrice = 0;
      if (booking.isMultipleCombine && timeSlots.length > 0) {
        totalPrice = timeSlots.reduce((sum, slot) => sum + (parseFloat(slot.price) || 0), 0);
      } else {
        totalPrice = parseFloat(booking.price) || 0;
      }

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL AMOUNT:', 10, yPosition);
      doc.setFontSize(12);
      doc.setTextColor(...primaryColor);
      doc.text(`${totalPrice} PKR`, pageWidth - 10, yPosition, { align: 'right' });
      yPosition += 8;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...darkColor);
      doc.text('Payment Method: Cash', 10, yPosition);

      // ==================== FOOTER ====================
      const footerHeight = 20;
      const footerY = pageHeight - footerHeight;

      doc.setDrawColor(...borderGray);
      doc.line(10, footerY - 2, pageWidth - 10, footerY - 2);
      doc.setFillColor(248, 248, 248);
      doc.rect(0, footerY, pageWidth, footerHeight, 'F');

      let footerTextY = footerY + 8;
      doc.setFontSize(8);
      doc.setTextColor(...darkColor);
      doc.setFont('helvetica', 'bold');
      doc.text('Thank you for your booking!', pageWidth / 2, footerTextY, { align: 'center' });
      footerTextY += 5;

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 120, 120);
      doc.text('This is a computer-generated receipt.', pageWidth / 2, footerTextY, { align: 'center' });

      // ==================== FACILITY DETAILS (bottom left, above footer) ====================
      const facilityLocationRaw =
        booking.slot?.facility?.location?.name ||
        booking.slot?.facility?.location?.address ||
        getFacilityLocation(booking, facilities) ||
        'N/A';
      const facilityLocation = sanitizeTextForPDF(facilityLocationRaw);

      // Get facility object for bank details
      const facilityObject = getFacilityObject(booking, facilities);

      const bankDetails = facilityObject?.bankDetails || null;

      // Position facility details just above the footer line
      let facilityY = footerY - 35; // Start 35mm above footer
      const bankDetailsX = pageWidth / 2 + 15; // Right side starting position for bank details
      const columnWidth = pageWidth / 2 - 15; // Width for each column

      // FACILITY DETAILS (Left Side)
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkColor);
      doc.text('FACILITY DETAILS', 10, facilityY);
      facilityY += 5;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');

      doc.text(`Name: ${sanitizedFacility}`, 10, facilityY);
      facilityY += 4;

      const locationLines = doc.splitTextToSize(`Location: ${facilityLocation}`, columnWidth);
      locationLines.forEach((line) => {
        doc.text(line, 10, facilityY);
        facilityY += 4;
      });

      const mapsLink = generateGoogleMapsLink(booking);
      if (mapsLink) {
        facilityY += 1;
        doc.setTextColor(66, 133, 244);
        doc.setFont('helvetica', 'bold');
        const linkText = 'View Location on Google Maps';
        const linkWidth = doc.getTextWidth(linkText);
        doc.link(10, facilityY - 3, linkWidth, 5, { url: mapsLink });
        doc.text(linkText, 10, facilityY);
      }

      // ==================== BANK DETAILS (bottom right, above footer) ====================
      let bankY = footerY - 35; // Start at same Y position as Facility Details

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkColor);
      doc.text('BANK DETAILS', bankDetailsX, bankY);
      bankY += 5;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');

      if (bankDetails) {
        if (bankDetails.bankName) {
          doc.text(`Bank Name: ${sanitizeTextForPDF(bankDetails.bankName)}`, bankDetailsX, bankY);
          bankY += 4;
        }
        if (bankDetails.accountNumber) {
          doc.text(`Account #: ${sanitizeTextForPDF(bankDetails.accountNumber)}`, bankDetailsX, bankY);
          bankY += 4;
        }
        if (bankDetails.accountHolderName) {
          doc.text(`Account Holder: ${sanitizeTextForPDF(bankDetails.accountHolderName)}`, bankDetailsX, bankY);
          bankY += 4;
        }
        if (bankDetails.iban) {
          doc.text(`IBAN: ${sanitizeTextForPDF(bankDetails.iban)}`, bankDetailsX, bankY);
          bankY += 4;
        }
        if (bankDetails.ntn) {
          doc.text(`NTN: ${sanitizeTextForPDF(bankDetails.ntn)}`, bankDetailsX, bankY);
          bankY += 4;
        }

        // If no bank details are available, show N/A
        if (!bankDetails.bankName && !bankDetails.accountNumber && !bankDetails.accountHolderName &&
          !bankDetails.iban && !bankDetails.ntn) {
          doc.text('N/A', bankDetailsX, bankY);
        }
      } else {
        doc.text('N/A', bankDetailsX, bankY);
      }

      // Save PDF
      const fileName = `Booking-${booking.bookingId || 'Receipt'}-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      notify("PDF exported successfully!", "success");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      notify("Failed to export PDF", "error");
    }
  };
  // Calculate pagination
  const indexOfLastBooking = currentPage * bookingsPerPage;
  const indexOfFirstBooking = indexOfLastBooking - bookingsPerPage;
  const currentBookings = filteredBookings.slice(indexOfFirstBooking, indexOfLastBooking);
  const totalPages = Math.ceil(filteredBookings.length / bookingsPerPage);

  // Handle page changes
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Render pagination controls
  const renderPagination = () => {
    // Remove the early return for single page
    // if (totalPages <= 1) return null;

    return (
      <div className="pagination-container">
        <button
          className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <div className="pagination-numbers">
          {[...Array(Math.max(1, totalPages))].map((_, index) => (
            <button
              key={index + 1}
              className={`page-number ${currentPage === index + 1 ? 'active' : ''}`}
              onClick={() => handlePageChange(index + 1)}
              disabled={totalPages <= 1}
            >
              {index + 1}
            </button>
          ))}
        </div>
        <button
          className={`pagination-btn ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          Next
        </button>
      </div>
    );
  };

  // Update pagination when filters change
  useEffect(() => {
    setCurrentIndex(0);
  }, [searchId, selectedMonth, selectedSport, selectedFacility, activeTab]);

  // Handle filter changes with animation
  const handleFilterChange = (filterType, value) => {
    setIsFiltering(true);

    // Update the appropriate filter state
    switch (filterType) {
      case 'searchId':
        setSearchId(value);
        break;
      case 'month':
        setSelectedMonth(value);
        break;
      case 'sport':
        setSelectedSport(value);
        break;
      case 'facility':
        setSelectedFacility(value);
        break;
      default:
        break;
    }

    // Reset to first page
    setCurrentPage(1);

    // Add a small delay to show the filtering animation
    setTimeout(() => {
      setIsFiltering(false);
    }, 300);
  };

  // Get current page bookings after filtering
  // const currentBookings = filteredBookings.slice(
  //   currentIndex * cardsPerPage,
  //   (currentIndex + 1) * cardsPerPage
  // );

  // const totalPages = Math.ceil(filteredBookings.length / cardsPerPage);

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(fetchBookings()),
          dispatch(fetchAllFacilities())
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    fetchData();
  }, [dispatch]);

  // Add loading state for booking details
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);


  // Update handleViewDetails function
  const handleViewDetails = async (booking) => {
    setIsLoadingDetails(true);
    setSelectedBooking(null); // Clear previous booking
    setShowDetails(true);

    try {
      // Simulate loading time for the booking details
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSelectedBooking(booking);
    } catch (error) {
      console.error('Error loading booking details:', error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, totalPages - 1));
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  function formatTime(dateString) {
    if (!dateString) return "N/A";

    const simpleTimeRegex = /^[0-9]{1,2}:[0-9]{2}(\s?[APMapm]{2})?$/;
    if (simpleTimeRegex.test(dateString.trim())) {
      return dateString;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }

    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHour = hours % 12 || 12;
    const formattedMinutes = minutes < 10 ? "0" + minutes : minutes;
    return `${formattedHour}:${formattedMinutes} ${ampm}`;
  }

  function formatStartEndTime(startDateString, endDateString) {
    const startTime = formatTime(startDateString);
    const endTime = formatTime(endDateString);
    return `${startTime} - ${endTime}`;
  }

  // Format date helper (dd MMM yyyy)
  const formatDetailDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd MMM yyyy");
    } catch (error) {
      return dateString;
    }
  };


  // Helper: extract phone from booking (random user preferred)
  const getBookingPhone = (booking) => {
    if (!booking) return "";
    if (isRandomUser(booking)) {
      return booking.randomUser?.phone || "";
    }
    return booking.bookedBy?.phone || "";
  };

  // Build a WhatsApp link in an international, country-agnostic way (E.164)
  // Supports inputs like +<cc><nsn>, 00<cc><nsn>, <cc><nsn>, or local 0-leading numbers when country code is present elsewhere
  const buildWhatsAppLink = (booking) => {
    const raw = getBookingPhone(booking);
    if (!raw) return null;

    // Strip all non-digits except leading '+' for detection
    const cleaned = String(raw).trim();

    let digitsOnly = cleaned.replace(/\D/g, "");

    // Handle prefixes: 00..., +...
    if (cleaned.startsWith("+")) {
      // Already in +E.164 style, just digitsOnly already removed '+'
    } else if (digitsOnly.startsWith("00")) {
      // Convert 00 to +
      digitsOnly = digitsOnly.slice(2);
    }

    // If the number starts with a country code (most likely 1-3 digits) followed by subscriber number
    // We cannot reliably guess a missing country code, so if it looks like a pure local (leading 0) we drop the 0,
    // but we DO NOT prepend any default country code. That would be incorrect for non-PK users.
    if (digitsOnly.startsWith("0")) {
      // Remove just the leading 0 to avoid wa.me rejecting it, but this still may be invalid without country code
      digitsOnly = digitsOnly.replace(/^0+/, "");
    }

    // If after cleanup the length is out of E.164 bounds, bail out
    // E.164 max length is 15 digits; min practical length is 8
    if (digitsOnly.length < 8 || digitsOnly.length > 15) {
      return null;
    }

    // Build wa.me link with the numeric digits. WhatsApp expects an international format number
    // without plus sign in the path. If the DB stored +CC..., we already removed '+' above.
    return `https://wa.me/${digitsOnly}`;
  };

  const handleOpenWhatsApp = (booking) => {
    const phone = getBookingPhone(booking);

    // Check if phone field is missing or empty
    if (!phone || phone.trim() === "" || phone === "N/A") {
      toast.error("Phone number not available for this user");
      return;
    }

    const link = buildWhatsAppLink(booking);

    if (link) {
      window.open(link, "_blank", "noopener,noreferrer");
    } else {
      // If phone exists but link couldn't be built, just open WhatsApp with the raw number
      // This handles cases where the number might not be perfectly formatted but could still work
      const cleanedPhone = phone.replace(/\D/g, "");
      if (cleanedPhone.length >= 8) {
        window.open(`https://wa.me/${cleanedPhone}`, "_blank", "noopener,noreferrer");
      } else {
        toast.error("Invalid phone number format for WhatsApp");
      }
    }
  };

  // Helper function to get time slots for booking (single or multi-slot)
  const getBookingTimeSlots = (booking) => {
    if (booking.isMultipleCombine && booking.individualPrices && booking.individualPrices.length > 0) {
      // Multi-slot booking - return individual time slots
      return booking.individualPrices.map((slot, index) => ({
        id: index,
        from: slot.from,
        to: slot.to,
        price: slot.price,
        slotId: slot.slotId
      }));
    } else {
      // Single slot booking - return single time slot
      return [{
        id: 0,
        from: booking.startTime,
        to: booking.endTime,
        price: booking.price,
        slotId: null
      }];
    }
  };

  const handleStartMessages = (booking) => {
    // const userName = getUserData(booking).name || "";
    const user = getUserData(booking);
    const userId = booking.bookedBy?._id;
    const userName = user.name || "";
    navigate("/messages", {
      state: {
        preselectUserId: userId,
        preselectUserName: userName,
        preselectUserData: user
      }
    });
  };

  // Render loading skeletons
  const renderSkeletons = () => {
    return (
      <Row>
        {Array.from({ length: cardsPerPage }).map((_, index) => (
          <Col md={4} key={`skeleton-${index}`} className="mb-4">
            <BookingCard isLoading={true} />
          </Col>
        ))}
      </Row>
    );
  };

  // Render error state
  const renderError = () => {
    return (
      <Row>
        <Col className="empty-bookings">
          <div className="text-center">
            <img src={Calender} alt="Error" className="empty-icon" />
            <p className="mt-3 text-muted">
              Unable to load bookings. Please check your internet connection and try again.
            </p>
            <Button
              variant="outline-success"
              onClick={() => {
                setIsInitialLoad(true);
                dispatch(fetchBookings());
                dispatch(fetchAllFacilities());
              }}
            >
              Retry
            </Button>
          </div>
        </Col>
      </Row>
    );
  };

  // Render empty state
  const renderEmpty = () => {
    const getEmptyMessage = () => {
      if (bookings.length === 0) {
        return "No recent bookings";
      }

      if (activeTab === 'current') {
        return "No current bookings match your filters";
      } else if (activeTab === 'past') {
        return "No past bookings match your filters";
      } else if (activeTab === 'cancelled') {
        return "No cancelled bookings match your filters";
      }

      return "No bookings match your filters";
    };

    return (
      <Col className="empty-bookings">
        <div className="text-center">
          <img src={Calender} alt="No Bookings" className="empty-icon" />
          <p className="mt-3 text-muted">
            {getEmptyMessage()}
          </p>
        </div>
      </Col>
    );
  };

  // Modify renderContent to use the new pagination
  const renderContent = () => {
    if (isInitialLoad || status === 'loading') {
      return (
        <>
          {renderSkeletons()}
          {renderPagination()} {/* Show pagination even during loading */}
        </>
      );
    }

    if (status === 'failed' || error) {
      return (
        <>
          {renderError()}
          {renderPagination()} {/* Show pagination even during error */}
        </>
      );
    }

    if (filteredBookings.length === 0) {
      return (
        <>
          {renderEmpty()}
          {renderPagination()} {/* Show pagination even when empty */}
        </>
      );
    }

    return (
      <>
        <Row>
          {currentBookings.map((booking, index) => (
            <Col
              md={4}
              key={`booking-${index}-${booking.bookingId || index}`}
              className="mb-4"
            >
              <BookingCard
                booking={booking}
                isLoading={false}
                onViewDetails={handleViewDetails}
              />
            </Col>
          ))}
        </Row>
        {renderPagination()}
      </>
    );
  };

  return (
    <Container>
      {/* Loading Overlay */}
      {status === "loading" && (
        <div className="loader-overlay">
          <div className="loader"></div>
        </div>
      )}
      <h1 className="container-title">My Bookings</h1>

      {/* Booking Type Tabs */}
      <Row className="mb-4">
        <Col lg={12}>
          <div className="booking-tabs">
            <button
              className={`tab-button ${activeTab === 'current' ? 'active' : ''}`}
              onClick={() => setActiveTab('current')}
              disabled={isInitialLoad || status === 'loading'}
            >
              <img src={Calender} alt="Current" className="tab-icon" />
              Current Bookings
              <span className="tab-count">
                {bookings.filter(booking => !isBookingPast(booking) && !booking.isCancelled).length}
              </span>
            </button>
            <button
              className={`tab-button ${activeTab === 'past' ? 'active' : ''}`}
              onClick={() => setActiveTab('past')}
              disabled={isInitialLoad || status === 'loading'}
            >
              <img src={smallcal} alt="Past" className="tab-icon" />
              Past Bookings
              <span className="tab-count">
                {bookings.filter(booking => isBookingPast(booking) && !booking.isCancelled).length}
              </span>
            </button>
            <button
              className={`tab-button ${activeTab === 'cancelled' ? 'active' : ''}`}
              onClick={() => setActiveTab('cancelled')}
              disabled={isInitialLoad || status === 'loading'}
            >
              <img src={smallcal} alt="Cancelled" className="tab-icon" />
              Cancelled Bookings
              <span className="tab-count">
                {bookings.filter(booking => booking.isCancelled === true).length}
              </span>
            </button>
          </div>
        </Col>
      </Row>

      {/* Search & Filters */}
      <Row className="mb-4 dropdownscourt">
        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Search by booking id..."
            value={searchId}
            onChange={(e) => handleFilterChange('searchId', e.target.value)}
            disabled={isInitialLoad || status === 'loading'}
          />
        </Col>
        <Col md={3}>
          <Form.Select
            value={selectedMonth}
            onChange={(e) => handleFilterChange('month', e.target.value)}
            disabled={isInitialLoad || status === 'loading'}
          >
            <option value="">Select Month</option>
            {uniqueMonths.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select
            value={selectedSport}
            onChange={(e) => handleFilterChange('sport', e.target.value)}
            disabled={isInitialLoad || status === 'loading'}
          >
            <option value="">Select Sport</option>
            {uniqueSports.map((sport) => (
              <option key={sport} value={sport}>
                {sport}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select
            value={selectedFacility}
            onChange={(e) => handleFilterChange('facility', e.target.value)}
            disabled={isInitialLoad || status === 'loading'}
          >
            <option value="">Select Facility</option>
            {uniqueFacilities.map((facility) => (
              <option key={facility._id} value={facility._id}>
                {facility.name}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Main Content */}
      {renderContent()}

      {/* Booking Details Side Panel */}
      <Offcanvas
        show={showDetails}
        onHide={() => {
          setShowDetails(false);
          setSelectedBooking(null);
          setIsLoadingDetails(false);
        }}
        placement="end"
        className="custom-offcanvas"
      >
        <Row>
          <Col lg={12}>
            <Offcanvas.Title className="canvas-title">
              Booking Details{" "}
              {!isLoadingDetails && selectedBooking && (
                <span className="booking-id-info">
                  Booking ID# {selectedBooking.bookingId || "N/A"}
                </span>
              )}
              {!isLoadingDetails && selectedBooking && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                  <Button
                    className="booking-export-pdf-btn"
                    onClick={() => exportBookingToPDF(selectedBooking)}
                    title="Export as PDF Invoice/Receipt"
                    variant="danger"
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Export PDF
                  </Button>
                  <Button
                    className="booking-copy-btn"
                    onClick={copyBookingToClipboard}
                    title="Copy booking details"
                  >
                    <img src={CopyIcon} className="booking-copy-icon" alt="Copy" /> Booking Info
                  </Button>
                </div>
              )}
            </Offcanvas.Title>
          </Col>
        </Row>

        <Offcanvas.Body style={{ paddingTop: '0px' }}>
          {isLoadingDetails ? (
            <BookingDetailsSkeleton />
          ) : selectedBooking ? (
            <div className="booking-details">
              {/* 1. Booked By Section - On Top */}
              <h3 className="booking-title-head">Booked By</h3>
              <div className="booking-imp-details">
                <Row>
                  <Col lg={6}>
                    <div className="schd-details">
                      <img src={profilePlaceholder} alt="Profile" />
                      <span className="booking-date-schd">Name</span>
                    </div>
                    <p className="data-schd">{getUserData(selectedBooking).name}</p>
                  </Col>
                  <Col lg={6}>
                    <div className="schd-details">
                      <img src={smallid} alt="Booking ID" />
                      <span className="booking-date-schd">Booking ID</span>
                    </div>
                    <p className="data-schd">{selectedBooking.bookingId || "N/A"}</p>
                  </Col>
                  <hr />
                </Row>

                {isRandomUser(selectedBooking) ? (
                  // For random users: show phone
                  <Row>
                    <Col lg={6}>
                      <div className="schd-details">
                        <img src={phoneicon} alt="Phone" />
                        <span className="booking-date-schd">Phone</span>
                      </div>
                      <p className="data-schd">{getUserData(selectedBooking).phone}</p>
                    </Col>
                    <Col lg={6}>
                      <div className="schd-details">
                        <img src={emailicon} alt="Phone" />
                        <span className="booking-date-schd">Email</span>
                      </div>
                      <p className="data-schd">{getUserData(selectedBooking).email}</p>
                      {/* Empty column for alignment */}
                    </Col>
                    <hr />
                  </Row>
                ) : (
                  // For app users: show gender, age, email, phone
                  <>
                    <Row>
                      <Col lg={6}>
                        <div className="schd-details">
                          <img src={gendericon} alt="Gender" />
                          <span className="booking-date-schd">Gender</span>
                        </div>
                        <p className="data-schd">{getUserData(selectedBooking).gender}</p>
                      </Col>
                      <Col lg={6}>
                        <div className="schd-details">
                          <img src={smallcal} alt="Age" />
                          <span className="booking-date-schd">Age</span>
                        </div>
                        <p className="data-schd">{getUserData(selectedBooking).age}</p>
                      </Col>
                      <hr />
                    </Row>

                    <Row>
                      <Col lg={6}>
                        <div className="schd-details">
                          <img src={emailicon} alt="Email" />
                          <span className="booking-date-schd">Email</span>
                        </div>
                        <p className="data-schd">{getUserData(selectedBooking).email}</p>
                      </Col>
                      <Col lg={6}>
                        <div className="schd-details">
                          <img src={phoneicon} alt="Phone" />
                          <span className="booking-date-schd">Phone</span>
                        </div>
                        <p className="data-schd">{getUserData(selectedBooking).phone}</p>
                      </Col>
                      <hr />
                    </Row>
                  </>
                )}

                <Row>
                  <Col lg={6}>
                    <div className="schd-details">
                      <img src={whatsapp} alt="WhatsApp" />
                      <span className="booking-date-schd">WhatsApp</span>
                    </div>
                    <p className="data-schd">
                      <Button
                        className="btn btn-success btn-sm"
                        onClick={() => handleOpenWhatsApp(selectedBooking)}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          borderRadius: '4px',
                          border: 'none'
                        }}
                      >
                        Open WhatsApp
                      </Button>
                    </p>
                  </Col>
                  {!isRandomUser(selectedBooking) && (
                    <Col lg={6}>
                      <div className="schd-details">
                        <img src={players} alt="Message" />
                        <span className="booking-date-schd">Message</span>
                      </div>
                      <p className="data-schd">
                        <Button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleStartMessages(selectedBooking)}
                          style={{
                            padding: '4px 12px',
                            fontSize: '12px',
                            borderRadius: '4px',
                            border: 'none'
                          }}
                        >
                          Send Message
                        </Button>
                      </p>
                    </Col>
                  )}
                  <hr />
                </Row>
              </div>

              {/* 2. Slot Information Section - In Middle */}
              <h3 className="booking-title-head">Slot Information</h3>
              <div className="booking-imp-details">
                <Row>
                  <Col lg={6}>
                    <div className="schd-details">
                      <img src={dateicon} alt="Date" /><span className="booking-date-schd">Date</span>
                    </div>
                    <p className="data-schd">{formatDetailDate(selectedBooking.selectedDate)}</p>
                  </Col>
                  <Col lg={6}>
                    <div className="schd-details">
                      <img src={timeicon} alt="Time" /><span className="booking-date-schd">Booking Time</span>
                    </div>
                    <p className="data-schd">
                      {selectedBooking.startTime && selectedBooking.endTime
                        ? `${formatTime(selectedBooking.startTime)} - ${formatTime(selectedBooking.endTime)}`
                        : "N/A"}
                    </p>
                  </Col>
                  <hr />
                </Row>

                <Row>
                  <Col lg={6}>
                    <div className="schd-details">
                      <img src={sportsicon} alt="Sport" /><span className="booking-date-schd">Sports</span>
                    </div>
                    <p className="data-schd">{getSportName(selectedBooking)}</p>
                  </Col>
                  <Col lg={6}>
                    <div className="schd-details">
                      <img src={pitch} alt="Pitch" /><span className="booking-date-schd">Pitch/Court</span>
                    </div>
                    <p className="data-schd">{getPitchName(selectedBooking) || "N/A"}</p>
                  </Col>
                  <hr />
                </Row>

                <Row>
                  <Col lg={6}>
                    <div className="schd-details">
                      <img src={facilityflag} alt="Facility" /><span className="booking-date-schd">Facility</span>
                    </div>
                    <p className="data-schd">{getFacilityName(selectedBooking, facilities) || "N/A"}</p>
                  </Col>
                  <Col lg={6}>
                    <div className="schd-details">
                      <img src={priceicon} alt="Price" /><span className="booking-date-schd">Price</span>
                    </div>
                    <p className="data-schd">{selectedBooking.price ? `${selectedBooking.price} PKR (Cash)` : "N/A"}</p>
                  </Col>
                  <hr />
                </Row>
              </div>

              {/* 3. Slot Details Section - At Bottom */}
              <h3 className="booking-title-head">Slot Details</h3>
              <div className="schedule-information">

                {/* Show total slots count for multi-slot bookings */}
                {selectedBooking.isMultipleCombine && selectedBooking.totalSlots && (
                  <Row style={{}}>
                    <Col lg={12}>
                      <div className="info-item">
                        <div className="content-wrapper">
                          {/* <p className="booking-info-label info-data">Total Slots</p> */}
                          <span className="side-panel-span">
                            {selectedBooking.totalSlots} slots combined
                          </span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}

                {/* Time Slots */}
                {getBookingTimeSlots(selectedBooking).map((timeSlot, index) => (
                  <Row key={timeSlot.id || index} style={{ margin: 0 }}>
                    <Col lg={12}>
                      <div className="time-slot-card">
                        <div className="time-slot-info">
                          <Form style={{ margin: 0 }}>
                            <Form.Check
                              type="radio"
                              id={`option${index}`}
                              label={`${formatStartEndTime(timeSlot.from, timeSlot.to)}`}
                              name="radioGroup"
                              className="green-radio"
                              checked={true}
                              readOnly
                              style={{ fontSize: '12px', fontWeight: '500' }}
                            />
                          </Form>
                        </div>

                        {/* Show individual price for multi-slot bookings - aligned right */}
                        {selectedBooking.isMultipleCombine && (
                          <div style={{ fontSize: '15px', color: '#28a745' }} className="time-slot-price">
                            {timeSlot.price} PKR
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                ))}
              </div>
            </div>
          ) : (
            <div className="no-booking-selected">
              <p>Select a booking to view details</p>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
      {/* Add styles for pagination */}
      <style jsx>{`
        .pagination-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-top: 2rem;
          gap: 1rem;
          padding-bottom: 1rem;
        }

        .pagination-numbers {
          display: flex;
          gap: 0.5rem;
        }

        .pagination-btn {
          padding: 8px 16px;
          border: 1px solid #e0e0e0;
          background: white;
          border-radius: 4px;
          cursor: pointer;
          color: #333;
          transition: all 0.2s;
          min-width: 100px;
          text-align: center;
        }

        .pagination-btn:hover:not(.disabled) {
          background: #f5f5f5;
        }

        .pagination-btn.disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: #f5f5f5;
          border-color: #e0e0e0;
        }

        .page-number {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
        }

        .page-number:hover:not(:disabled) {
          background: #f5f5f5;
        }

        .page-number:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: #f5f5f5;
        }

        .page-number.active {
          background: #38b000;
          color: white;
          border-color: #38b000;
        }

        .page-number.active:disabled {
          opacity: 1;
        }

        .booking-cards {
          // min-height: 400px;
        }

        .booking-details .skeleton {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: loading 1.5s infinite;
        }

        @keyframes loading {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }

        .personal-data-wrap .skeleton {
          margin-bottom: 8px;
        }

        .info-item .skeleton {
          display: inline-block;
          vertical-align: middle;
        }

        .schedule-wrap .skeleton {
          margin-bottom: 8px;
        }

        .no-booking-selected {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100%;
          color: #666;
          text-align: center;
          padding: 2rem;
        }

        .no-booking-selected p {
          margin: 0;
          font-size: 1rem;
        }

        /* Booking Tabs Styles */
        .booking-tabs {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
          border-bottom: 2px solid #f0f0f0;
          padding-bottom: 0;
        }

        .tab-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          border: none;
          background: transparent;
          color: #666;
          font-weight: 500;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s ease;
          border-bottom: 3px solid transparent;
          position: relative;
          min-width: 160px;
          justify-content: center;
        }

        .tab-button:hover:not(:disabled) {
          color: #38b000;
          background: rgba(56, 176, 0, 0.05);
        }

        .tab-button.active {
          color: #38b000;
          border-bottom-color: #38b000;
          background: rgba(56, 176, 0, 0.08);
        }

        .tab-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .tab-icon {
          width: 16px;
          height: 16px;
          object-fit: contain;
        }

        .tab-count {
          background: #38b000;
          color: white;
          border-radius: 12px;
          padding: 2px 8px;
          font-size: 12px;
          font-weight: 600;
          min-width: 20px;
          text-align: center;
        }

        .tab-button:not(.active) .tab-count {
          background: #e0e0e0;
          color: #666;
        }

        /* Responsive tabs */
        @media (max-width: 768px) {
          .booking-tabs {
            flex-direction: column;
            gap: 8px;
          }

          .tab-button {
            min-width: auto;
            justify-content: flex-start;
            padding: 10px 16px;
          }
        }

        /* Update skeleton styles */
        .skeleton {
          background: linear-gradient(
            90deg,
            rgba(190, 190, 190, 0.2) 25%,
            rgba(129, 129, 129, 0.24) 37%,
            rgba(190, 190, 190, 0.2) 63%
          );
          background-size: 400% 100%;
          animation: skeleton-loading 1.4s ease infinite;
        }

        @keyframes skeleton-loading {
          0% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0 50%;
          }
        }

        .booking-details .skeleton {
          border-radius: 4px;
        }

        .personal-data-wrap .skeleton {
          margin-bottom: 8px;
        }

        .info-item .skeleton {
          display: inline-block;
          vertical-align: middle;
        }

        .schedule-wrap .skeleton {
          margin-bottom: 8px;
        }

        /* Badge container styles */
        .badge-container {
          display: flex;
          flex-direction: column;
          gap: 4px;
          align-items: flex-end;
        }

         /* Multi-slot badge styles */
         .multi-slot-badge {
           background: #e1f8d6;
           border-radius: 20px;
           padding: 4px 10px;
           border: 1px solid #37b000;
         }

         .multi-slot-text {
           color: #37b000;
           font-size: 11px;
           font-weight: 600;
           letter-spacing: 0.5px;
           text-transform: uppercase;
         }

         /* Single-slot badge styles */
         .single-slot-badge {
           background: #e1f8d6;
           border-radius: 20px;
           padding: 4px 10px;
           border: 1px solid #37b000;
         }

         .single-slot-text {
           color: #37b000;
           font-size: 11px;
           font-weight: 600;
           letter-spacing: 0.5px;
           text-transform: uppercase;
         }

         /* Reserved badge styles */
         .reserved-badge {
           background: #fff3cd;
           border-radius: 20px;
           padding: 4px 10px;
           border: 1px solid #ffc107;
         }

         .reserved-text {
           color: #856404;
           font-size: 11px;
           font-weight: 600;
           letter-spacing: 0.5px;
           text-transform: uppercase;
         }

         /* Cancelled style overrides for existing badges */
         .multi-slot-badge.cancelled-style,
         .single-slot-badge.cancelled-style,
         .reserved-badge.cancelled-style {
           background: #f8d7da !important;
           border: 1px solid #dc3545 !important;
         }

         .multi-slot-text.cancelled-text,
         .single-slot-text.cancelled-text,
         .reserved-text.cancelled-text {
           color: #721c24 !important;
         }

         /* Cancellation type badge styles */
         .cancellation-type-badge {
           border-radius: 20px;
           padding: 4px 10px;
           border: 1px solid;
         }

         .cancellation-type-badge.user {
           background: #fff3cd;
           border-color: #ffc107;
         }

         .cancellation-type-badge.facility {
           background: #f8d7da;
           border-color: #dc3545;
         }

         .cancellation-type-badge.unknown {
           background: #d1ecf1;
           border-color: #17a2b8;
         }

         .cancellation-type-text {
           font-size: 11px;
           font-weight: 600;
           letter-spacing: 0.5px;
           text-transform: uppercase;
         }

         .cancellation-type-text.user {
           color: #856404;
         }

         .cancellation-type-text.facility {
           color: #721c24;
         }

         .cancellation-type-text.unknown {
           color: #0c5460;
         }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .multi-slot-badge,
          .single-slot-badge,
          .reserved-badge,
          .cancellation-type-badge {
            padding: 3px 8px;
          }
          
          .multi-slot-text,
          .single-slot-text,
          .reserved-text,
          .cancellation-type-text {
            font-size: 10px;
          }
          
          .badge-container {
            gap: 3px;
          }
        }
      `}</style>
    </Container>
  );
};

export default MyBookings;